import { defineStore } from 'pinia';
import {
    mapSchoolRoles,
    type FlightSchoolUser,
    mapSchoolCourses,
    FlightSchoolRole,
    mapRoles,
} from '@/types/FlightSchoolUser';
import type { AuthenticationResponse } from '@/types/Response/AuthenticationResponse';
import { toTitleCase } from '@/functions/toTitleCase';

export const useUserStore = defineStore({
    id: 'flightSchoolUser',
    state: () => ({
        user: null as FlightSchoolUser|null,
        userName: '',
        userEmail: '',
    }),
    actions: {
        authenticate(data: AuthenticationResponse) {
            if (data?.user !== undefined) {
                this.user = {
                    id: parseInt(data.user.id, 10),
                    roles: mapRoles(data.user.roles),
                    schoolRoles: mapSchoolRoles(data.user.schoolRoles),
                    schoolCourses: mapSchoolCourses(data.user.schoolCourses),
                    accessDate: Date.now(),
                };

                this.serialize();
            }
        },
        deserialize() {
            // prevent more expensive calls to LocalStorage if possible
            if (!this.user) {
                const user = localStorage.getItem('flightSchoolUser');
                if (user !== null) {
                    this.user = JSON.parse(user);
                }
            }
        },
        serialize() {
            const user = JSON.stringify(this.user);
            localStorage.setItem('flightSchoolUser', user);
        },
        logout() {
            this.user = null;
            localStorage.removeItem('flightSchoolUser');
        },
        // user is considered to be authenticated while they still have acess remaining
        isAuthenticated(): boolean {
            /**
             * Before any authentication, check if there is currently a user in LocalStorage
             */
            this.deserialize();
            if (this.user) {
                const timeElapsed = (Date.now() - this.user.accessDate) / 1000;
                return timeElapsed < import.meta.env.VITE_TIMEOUT;
            }

            return false;
        },
        isSchoolOwnerForSchool(schoolId: number): boolean {
            return this.user?.schoolRoles.some(
                (role) => role.role === FlightSchoolRole.OWNER && role.schools.includes(schoolId),
            ) ?? false;
        },
        canAdministerSchool(schoolId: number): boolean {
            return this.isGlobalAdmin || this.isSchoolOwnerForSchool(schoolId);
        },
        setUserInfo(name: string, email: string) {
            this.userName = toTitleCase(name);
            this.userEmail = email;
        },
    },
    getters: {
        isGlobalAdmin(): boolean {
            return this.user?.roles.includes(FlightSchoolRole.GLOBAL) ?? false;
        },
        isSchoolOwner(): boolean {
            return this.user?.roles.includes(FlightSchoolRole.OWNER) ?? false;
        },
        isCourseAdmin(): boolean {
            return this.user?.roles.includes(FlightSchoolRole.ADMIN) ?? false;
        },
        printableUserRole(): string {
            if (this.isGlobalAdmin) {
                return 'Global Admin';
            }
            if (this.isSchoolOwner) {
                return 'Primary Admin';
            }

            return 'Course Admin';
        },
    },
});
